import { ArrowClockwiseIcon, WarningCircleIcon } from "@phosphor-icons/react";
import type { ReactNode } from "react";

import { cn } from "../cn.js";
import { Button } from "./Button.js";

/**
 * Loading, empty and error states.
 *
 * Every list and every mutation in the product needs all three (spec §76), so
 * they live here rather than being improvised per screen.
 */

export interface SkeletonProps {
	className?: string;
	/** Announced to screen readers while content loads. */
	label?: string;
}

export function Skeleton({ className, label }: SkeletonProps) {
	return (
		<div
			role={label ? "status" : undefined}
			aria-label={label}
			className={cn("animate-pulse rounded-(--radius-sm) bg-(--surface-sunken)", className)}
		/>
	);
}

export interface SkeletonRowsProps {
	rows?: number;
	className?: string;
}

/** Stable-height placeholder, so content does not jump when it arrives. */
export function SkeletonRows({ rows = 5, className }: SkeletonRowsProps) {
	return (
		<div
			className={cn("flex w-full max-w-full shrink-0 flex-col gap-px", className)}
			role="status"
			aria-label="Loading"
		>
			{Array.from({ length: rows }, (_, index) => (
				<div key={index} className="flex items-center gap-3 px-4 py-3">
					<Skeleton className="size-8 shrink-0 rounded-full" />
					<div className="flex min-w-0 flex-1 flex-col gap-1.5">
						<Skeleton className="h-3.5 w-full max-w-[10rem]" />
						<Skeleton className="h-3 w-full max-w-[14rem]" />
					</div>
					<Skeleton className="h-3.5 w-16 shrink-0" />
				</div>
			))}
		</div>
	);
}

export interface EmptyStateProps {
	icon?: ReactNode;
	title: string;
	/** Say what to do next, not merely that there is nothing here. */
	description: string;
	action?: ReactNode;
	className?: string;
}

export function EmptyState({ icon, title, description, action, className }: EmptyStateProps) {
	return (
		<div className={cn("flex flex-col items-center gap-3 px-6 py-12 text-center", className)}>
			{icon && <div className="text-(--text-muted)">{icon}</div>}
			<div className="flex flex-col gap-1.5">
				<p className="text-sm font-medium text-(--text-primary)">{title}</p>
				<p className="mx-auto max-w-sm text-sm text-(--text-secondary)">{description}</p>
			</div>
			{action && <div className="mt-1">{action}</div>}
		</div>
	);
}

export interface ErrorStateProps {
	/** Plain-language summary. Never a raw error code. */
	title: string;
	description?: string;
	/** Technical detail, disclosed only on request. */
	detail?: string | null;
	requestId?: string | null;
	onRetry?: () => void;
	retryPending?: boolean;
	className?: string;
}

export function ErrorState({
	title,
	description,
	detail,
	requestId,
	onRetry,
	retryPending,
	className,
}: ErrorStateProps) {
	return (
		<div
			role="alert"
			className={cn(
				"flex flex-col items-start gap-3 rounded-(--radius-lg) border border-(--color-critical-500)/25",
				"bg-(--color-critical-50) p-4",
				className,
			)}
		>
			<div className="flex items-start gap-2.5">
				<WarningCircleIcon
					className="mt-0.5 size-5 shrink-0 text-(--color-critical-600)"
					weight="fill"
					aria-hidden="true"
				/>
				<div className="flex flex-col gap-1">
					<p className="text-sm font-medium text-(--color-critical-700)">{title}</p>
					{description && <p className="text-sm text-(--color-critical-700)/90">{description}</p>}
				</div>
			</div>

			{onRetry && (
				<Button size="sm" variant="secondary" onClick={onRetry} pending={retryPending} iconLeft={<ArrowClockwiseIcon className="size-4" />}>
					Try again
				</Button>
			)}

			{(detail || requestId) && (
				<details className="w-full">
					<summary className="cursor-pointer text-xs text-(--color-critical-700)/80">
						Technical details
					</summary>
					<div className="mt-2 flex flex-col gap-1 text-dns text-(--color-critical-700)/90">
						{detail && <p>{detail}</p>}
						{requestId && <p>Reference: {requestId}</p>}
					</div>
				</details>
			)}
		</div>
	);
}

export interface InlineNoticeProps {
	tone: "info" | "caution" | "critical" | "positive";
	title?: string;
	children: ReactNode;
	action?: ReactNode;
	className?: string;
}

const NOTICE_TONES = {
	info: "border-(--color-brand-500)/25 bg-(--color-brand-50) text-(--color-brand-700)",
	caution: "border-(--color-caution-500)/30 bg-(--color-caution-50) text-(--color-caution-700)",
	critical: "border-(--color-critical-500)/25 bg-(--color-critical-50) text-(--color-critical-700)",
	positive: "border-(--color-positive-500)/25 bg-(--color-positive-50) text-(--color-positive-700)",
} as const;

export function InlineNotice({ tone, title, children, action, className }: InlineNoticeProps) {
	return (
		<div
			className={cn(
				"flex flex-wrap items-center justify-between gap-3 rounded-(--radius-md) border px-3.5 py-2.5 text-sm",
				NOTICE_TONES[tone],
				className,
			)}
		>
			<div className="flex flex-col gap-0.5">
				{title && <p className="font-medium">{title}</p>}
				<div>{children}</div>
			</div>
			{action}
		</div>
	);
}
